import { useCallback, useMemo } from "react";

import { create } from "@bufbuild/protobuf";
import { ArrowsCounterClockwiseIcon, SphereIcon } from "@phosphor-icons/react";

import Avatar from "@gx/dls/avatar/Avatar";
import Beacon from "@gx/dls/beacons/Beacon";
import Chip from "@gx/dls/chips/Chip";
import FlexItem from "@gx/dls/containers/FlexItem";
import FlexWrapper, { AlignItems, FlexDirection } from "@gx/dls/containers/FlexWrapper";
import InfiniteTable, { ColumnAlign, ColumnDef, ColumnPin } from "@gx/dls/table/InfiniteTable";
import Text, { TextSize, TextVariant, TextWeight } from "@gx/dls/text/Text";
import TextShimmer from "@gx/dls/text/TextShimmer";
import { SourceSyncStatus, SourceType } from "@gx/ts-contracts/services/database/models/public_pb";
import { GetSourceRequestSchema } from "@gx/ts-contracts/services/source/api/source_pb";
import { ListSourceSyncsRequestSchema } from "@gx/ts-contracts/services/source/api/source_sync_pb";
import { SourceResource } from "@gx/ts-contracts/services/source/resources/source_pb";

import { shouldRefetchSourceSyncs } from "@/pages/sources/components/edit/utils";
import SourceIcon, { SourceIconSize } from "@/pages/sources/components/SourceIcon";
import {
  SYNC_STATUS_TO_BEACON_VARIANT_MAP,
  SYNC_STATUS_TO_TEXT_MAP,
  SYNC_STATUS_TO_TEXT_VARIANT_MAP,
} from "@/pages/sources/constants";
import SourcesPageEmptyLayout from "@/pages/sources/views/SourcesPageEmptyLayout";

import { useGetSourceQuery } from "@/api/queries/sources";
import { useListSourceSyncsQuery } from "@/api/queries/syncs";

import { convertProtobufTimestampToDate, getTimeAgo } from "@/utils/datetimes";
import { hydrateDeletedSourceName } from "@/utils/source";
import { getUserFullName } from "@/utils/users";

const NameCell = ({ source }: { source: SourceResource }) => {
  return (
    <FlexWrapper gap={12} alignItems={AlignItems.CENTER} fillWidth>
      <FlexItem grow={0} shrink={0} display="flex">
        <SourceIcon
          sourceType={source.sourceType ?? SourceType.UNSPECIFIED}
          size={SourceIconSize.MEDIUM}
        />
      </FlexItem>
      <Text
        weight={TextWeight.MEDIUM}
        variant={source.isDeleted ? TextVariant.ERROR : TextVariant.PRIMARY}
        isEllipsis
      >
        {source.isDeleted ? hydrateDeletedSourceName(source.name) : source.name}
      </Text>
    </FlexWrapper>
  );
};

const NameCellLoading = () => <TextShimmer height={16} width={120} />;

const StatusCell = ({ source }: { source: SourceResource }) => {
  const { data: syncsData, isLoading: isLoadingSyncsData } = useListSourceSyncsQuery({
    input: create(ListSourceSyncsRequestSchema, {
      sourceId: source.id,
    }),
    options: {
      refetchInterval: (query) => shouldRefetchSourceSyncs(2000, query.state.data?.syncs),
    },
  });

  const latestSyncStatus = useMemo(() => {
    if (!syncsData?.syncs || syncsData.syncs.length === 0) {
      return SourceSyncStatus.UNSPECIFIED;
    }
    return syncsData.syncs[syncsData.syncs.length - 1].syncStatus;
  }, [syncsData?.syncs]);

  const renderStatus = () => {
    if (source.isDeleted) {
      return (
        <FlexWrapper gap={6} alignItems={AlignItems.CENTER}>
          <Text size={TextSize.BODY_SM} weight={TextWeight.MEDIUM} variant={TextVariant.ERROR}>
            Deleted
          </Text>
        </FlexWrapper>
      );
    }

    return (
      <FlexWrapper gap={6} alignItems={AlignItems.CENTER}>
        <Beacon variant={SYNC_STATUS_TO_BEACON_VARIANT_MAP[latestSyncStatus]} isPulse />
        <Text
          size={TextSize.BODY_SM}
          weight={TextWeight.MEDIUM}
          variant={SYNC_STATUS_TO_TEXT_VARIANT_MAP[latestSyncStatus]}
        >
          {SYNC_STATUS_TO_TEXT_MAP[latestSyncStatus]}
        </Text>
      </FlexWrapper>
    );
  };

  if (isLoadingSyncsData) {
    return <TextShimmer height={16} width={100} />;
  }

  return renderStatus();
};

const StatusCellLoading = () => <TextShimmer height={16} width={100} />;

const LastSyncedCell = ({ source }: { source: SourceResource }) => {
  const { data: sourceData, isLoading: isLoadingSourceData } = useGetSourceQuery({
    input: create(GetSourceRequestSchema, { sourceId: source.id, includeDeleted: true }),
    options: {
      enabled: true,
    },
  });

  if (isLoadingSourceData) {
    return <TextShimmer height={16} width={100} />;
  }

  const lastSyncedAt = sourceData?.source?.updatedAt;
  if (!lastSyncedAt) {
    return <Text variant={TextVariant.TERTIARY}>-</Text>;
  }

  return (
    <Text variant={TextVariant.SECONDARY}>
      {getTimeAgo(convertProtobufTimestampToDate(lastSyncedAt))}
    </Text>
  );
};

const LastSyncedCellLoading = () => <TextShimmer height={16} width={100} />;

const NumSyncsCell = ({ source }: { source: SourceResource }) => {
  const numSyncs = source?.metrics?.syncCount ?? 0n;

  return (
    <FlexWrapper>
      <Chip icon={ArrowsCounterClockwiseIcon} label={Number(numSyncs) || "—"} />
    </FlexWrapper>
  );
};

const NumSyncsCellLoading = () => <TextShimmer height={16} width={40} />;

const NumProjectsCell = ({ source }: { source: SourceResource }) => {
  const numProjects = source?.metrics?.projectCount ?? 0n;

  return (
    <FlexWrapper>
      <Chip icon={SphereIcon} label={Number(numProjects) || "—"} />
    </FlexWrapper>
  );
};

const NumProjectsCellLoading = () => <TextShimmer height={16} width={40} />;

const UserCell = ({ source }: { source: SourceResource }) => (
  <FlexWrapper alignItems={AlignItems.CENTER} gap={8} overflow="hidden">
    <FlexItem grow={0} shrink={0} display="flex">
      <Avatar size={16} img={source.createdBy?.avatarUrl} hex={source.createdBy?.avatarHex} />
    </FlexItem>
    <FlexItem grow={0} shrink={0} minWidth={0} overflow="hidden">
      <FlexWrapper direction={FlexDirection.COLUMN} gap={2} overflow="hidden">
        <Text variant={TextVariant.SECONDARY} isEllipsis>
          {getUserFullName(source.createdBy)}
        </Text>
        <Text size={TextSize.CAPTION} variant={TextVariant.TERTIARY} isEllipsis>
          {source.createdAt ? getTimeAgo(convertProtobufTimestampToDate(source.createdAt)) : "—"}
        </Text>
      </FlexWrapper>
    </FlexItem>
  </FlexWrapper>
);

const UserCellLoading = () => <TextShimmer height={16} width={120} />;

const SOURCES_TABLE_VIEW_COLUMNS: ColumnDef<SourceResource>[] = [
  {
    id: "name",
    header: "Name",
    size: 480,
    pin: ColumnPin.LEFT,
    cellLoading: NameCellLoading,
    cell: ({ row }) => <NameCell source={row.original} />,
  },
  {
    id: "lastSyncedAt",
    header: "Last synced",
    size: 180,
    cellLoading: LastSyncedCellLoading,
    cell: ({ row }) => <LastSyncedCell source={row.original} />,
  },
  {
    id: "status",
    header: "Status",
    cellLoading: StatusCellLoading,
    cell: ({ row }) => <StatusCell source={row.original} />,
  },
  {
    id: "user",
    header: "Created by",
    size: 180,
    cellLoading: UserCellLoading,
    cell: ({ row }) => <UserCell source={row.original} />,
  },
  {
    id: "num-projects",
    header: "Projects",
    size: 80,
    align: ColumnAlign.RIGHT,
    cellLoading: NumProjectsCellLoading,
    cell: ({ row }) => <NumProjectsCell source={row.original} />,
  },
  {
    id: "num-syncs",
    header: "Syncs",
    size: 80,
    align: ColumnAlign.RIGHT,
    cellLoading: NumSyncsCellLoading,
    cell: ({ row }) => <NumSyncsCell source={row.original} />,
  },
];

interface SourcesPageBodyTableProps {
  data: SourceResource[];
  isLoading: boolean;
  onRowClick: (row: { original: SourceResource }) => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
}

const SourcesPageBodyTable = ({
  data,
  isLoading,
  onRowClick,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: SourcesPageBodyTableProps) => {
  const handleRowClick = useCallback(
    (row: { original: SourceResource }) => {
      if (row.original.isDeleted) {
        return;
      }
      onRowClick(row);
    },
    [onRowClick],
  );

  return (
    <InfiniteTable<SourceResource>
      columns={SOURCES_TABLE_VIEW_COLUMNS}
      data={data}
      getRowId={(source) => source.id}
      fillWidth
      fillHeight
      onRowClick={handleRowClick}
      contentWhenEmpty={<SourcesPageEmptyLayout />}
      isLoading={isLoading}
      loadingRowCount={10}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
    />
  );
};

export default SourcesPageBodyTable;
